/**
 * Sales-tax liability ledger selector — Phase 10.
 *
 * Reads activity against 2310 Accrued Sales Tax and groups it by jurisdiction
 * (resolved from JE-line memo `tax:<jurisdictionId>`) and period (YYYY-MM).
 *
 * Convention:
 *   Collection / accrual posts:   CR 2310 (credit increases liability)
 *   Remittance posts:             DR 2310 (debit decreases liability)
 *
 * Lines without a tax: memo land under "unassigned" so the bookkeeper can fix.
 */
import type { JournalEntry } from "../glTypes";
import { parseJurisdictionFromMemo } from "./salesTaxConfig";

const TAX_ACCOUNT = "2310";

export interface TaxPeriodRow {
  period: string;        // "2026-06"
  jurisdictionId: string;
  collected: number;     // credits (positive)
  remitted: number;      // debits  (positive)
  balance: number;       // collected - remitted
}

export interface TaxJurisdictionSummary {
  jurisdictionId: string;
  collected: number;
  remitted: number;
  balance: number;
  oldestOpenPeriod?: string;
  periods: TaxPeriodRow[];
}

export function summarizeSalesTax(entries: JournalEntry[]): {
  totalAccrued: number;
  totalRemitted: number;
  openBalance: number;
  byJurisdiction: TaxJurisdictionSummary[];
  unassigned: { collected: number; remitted: number; balance: number };
} {
  const cells = new Map<string, TaxPeriodRow>(); // key = period|jur
  let unCollected = 0;
  let unRemitted = 0;

  for (const je of entries) {
    if (je.status !== "posted") continue;
    for (const line of je.lines) {
      if (line.accountId !== TAX_ACCOUNT) continue;
      const jur = parseJurisdictionFromMemo(line.memo) ?? "UNASSIGNED";
      const key = `${je.period}|${jur}`;
      const cur = cells.get(key) ?? {
        period: je.period, jurisdictionId: jur,
        collected: 0, remitted: 0, balance: 0,
      };
      if (line.side === "credit") cur.collected += line.amount;
      else cur.remitted += line.amount;
      cur.balance = cur.collected - cur.remitted;
      cells.set(key, cur);

      if (jur === "UNASSIGNED") {
        if (line.side === "credit") unCollected += line.amount;
        else unRemitted += line.amount;
      }
    }
  }

  const byJurMap = new Map<string, TaxJurisdictionSummary>();
  Array.from(cells.values())
    .sort((a, b) => a.period.localeCompare(b.period))
    .forEach(row => {
      if (row.jurisdictionId === "UNASSIGNED") return;
      const cur = byJurMap.get(row.jurisdictionId) ?? {
        jurisdictionId: row.jurisdictionId,
        collected: 0, remitted: 0, balance: 0,
        periods: [],
      };
      cur.collected += row.collected;
      cur.remitted += row.remitted;
      cur.balance = cur.collected - cur.remitted;
      cur.periods.push(row);
      if (!cur.oldestOpenPeriod && row.balance > 0.005) cur.oldestOpenPeriod = row.period;
      byJurMap.set(row.jurisdictionId, cur);
    });

  const byJurisdiction = Array.from(byJurMap.values())
    .sort((a, b) => b.balance - a.balance);

  const totalAccrued = byJurisdiction.reduce((s, j) => s + j.collected, 0) + unCollected;
  const totalRemitted = byJurisdiction.reduce((s, j) => s + j.remitted, 0) + unRemitted;

  return {
    totalAccrued,
    totalRemitted,
    openBalance: totalAccrued - totalRemitted,
    byJurisdiction,
    unassigned: {
      collected: unCollected,
      remitted: unRemitted,
      balance: unCollected - unRemitted,
    },
  };
}
